/**
 * Central game configuration — adjust reels, symbols, timing, and payouts here.
 */

/**
 * 8px grid — spacing, borders, radii, type (single source for all chrome).
 */
const ui = {
  panelPadX: 20,
  panelPadY: 14,
  /** Tallest stat card row; must fit label + inline bet chips + padding */
  statRowH: 76,
  rowGap: 12,
  cardGap: 10,
  cardPadX: 12,
  cardPadY: 10,
  /** Horizontal gap between stat label and value (BALANCE 12,345) */
  statInlineGap: 10,
  /** Vertical gap between BALANCE row and TOTAL BET row */
  statStackGap: 4,
  /** Extra letter-spacing on stat value numbers */
  statValueTracking: 0.4,
  /**
   * LAST WIN amount vertical placement in its column: fraction from label bottom toward inner bottom.
   * 0.5 = centered in band; higher = lower on screen.
   */
  lastWinVerticalBias: 0.62,
  /** Space between LAST WIN card and SPIN button */
  spinGap: 14,
  spinW: 160,
  spinH: 70,

  radiusPanel: 20,
  radiusCard: 14,
  radiusChip: 14,
  radiusSpin: 18,
  radiusWell: 12,
  radiusCabinet: 16,
  radiusColumn: 10,

  /** Border widths (px) — use everywhere for consistency */
  strokePanelOuter: 1,
  strokePanelInner: 1,
  strokeCardOuter: 2,
  strokeCardInner: 1,
  strokeChip: 2,
  strokeChipInner: 1,
  strokeSpinOuter: 3,
  strokeSpinInner: 1,
  strokeCabinetOuter: 3,
  strokeCabinetMid: 2,
  strokeCabinetInner: 1,
  strokeWell: 2,
  strokeWellInner: 1,
  strokeHeaderRule: 2,

  typeStatLabel: 10,
  typeStatValue: 20,
  /** LAST WIN card — main total (larger) */
  typeWinLastTotal: 22,
  /** Per-line amounts only, under total */
  typeWinDetail: 10,
  typeSpin: 26,
  typeBetGlyph: 22,
  typeHeaderTitle: 36,
  typeHeaderSub: 11,
  /** Header — prominent last win amount (right side) */
  typeHeaderWin: 30,

  /** Base art width for ± chips; row layout scales to fill card */
  betChipW: 46,
  betChipH: 44,
  betChipGap: 8,
  betChipMinW: 28,
  /** Target share of row width per ± button before middle field (0–0.5) */
  betChipSideRatio: 0.2,
  betInputMinW: 36,
  /** Vertical gap between stat label and ± chip row */
  betLabelChipGap: 10,

  /** Win line overlay on the reel grid */
  winLineGlowWidth: 10,
  winLineCoreWidth: 3,
  winLineJointRadius: 5,
  winLineLabelOffsetY: 32,

  gapCabinetToBar: 20,
  gapBarBottom: 16,
  /** Pixel inset of reel well around the symbol grid */
  wellInset: 6,
  /**
   * Extra pixels clipped off the bottom of the reel viewport mask so spinning symbols
   * disappear under the frame instead of drawing past it.
   */
  reelViewportMaskBottomExtra: 16,
  /**
   * Extra top clip on the reel viewport when reels are stopped — hides the strip symbol
   * peeking above row 0. Not applied while spinning.
   */
  reelViewportMaskTopExtraWhenIdle: 14,
  /**
   * Reel cabinet scale factor after the “fit to viewport” pass. 1 = max fit; below 1 = smaller grid (bottom bar unchanged).
   */
  slotCabinetScaleMul: 0.92,
};

export const gameConfig = {
  /** Logical grid */
  reelCount: 5,
  rowCount: 3,

  /** Layout — sized for fullscreen scale-up (Olympus-style cabinet) */
  cellWidth: 177,
  cellHeight: 177,
  reelGap: 0,
  machinePadding: 24,
  headerHeight: 84,
  /**
   * Bottom panel: one row (stats + inline bet + SPIN). Keeps in sync with ui padding
   * and the taller of the stat cards vs. the spin button.
   */
  controlBarHeight: ui.panelPadY * 2 + Math.max(ui.statRowH, ui.spinH),
  /** Marble column width on each side of the reel window */
  columnWidth: 48,

  ui,

  /** Strip length tuned down to reduce hidden Spine workload and startup hitches. */
  stripMinLength: 64,
  stripLandingOffsetMin: 7,
  stripLandingOffsetSpread: 4,
  /** Less strip travel at the same cruise px/s → shorter tier-1 spin without slowing the blur speed. */
  extraSpinCellsMin: 11,
  extraSpinCellsSpread: 5,

  spinPhaseAccelerate: 0,
  spinPhaseCruise: 0.26,
  /** Shorter final ease only; cruise velocity unchanged (see reelCruisePixelsPerSecond). */
  spinPhaseDecelerate: 0.22,
  /** +1 = current direction, -1 = opposite direction */
  reelSpinDirection: -1,
  /**
   * During a spin, only strip indices within roughly the visible window ± this many cells
   * keep live Spine instances (`Symbol#setActive`). Same motion and art; fewer off-screen rigs updating.
   */
  reelSpinViewportPadCells: 4,
  /** Extra seconds before each next column lands (all columns start together; only stops stagger L→R) */
  reelStopStaggerSec: 0.34,
  /**
   * Linear strip speed during the cruise phase (px/s), same for every reel.
   * Staggered stops add extra strip travel per column so decel starts later — same speed, not stretched time.
   * Locked to the former derived cap so tightening travel above does not reduce scroll speed.
   */
  reelCruisePixelsPerSecond: 3400,
  /** Cap when deriving cruise speed from spinPhaseCruise + typical travel (avoids huge stagger strip growth). */
  reelCruiseDerivedCapPxPerSec: 3400,
  /**
   * Bet-speed tier 2 (two arrows lit): multiplies `reelStopStaggerSec` only — tighter spacing between reel stops.
   * Scroll/decel speed stays identical to tier 1. Use 1 for same timing as tier 1; lower = stops closer together in time.
   */
  betSpeedTier2StaggerScale: 0.48,
  /** After tier-2 spin lands, brief pause before expanding-wild / result flow (ms). */
  betSpeedTier2ResultHoldMs: 400,
  /** If tier-2 win needs extra time before next phase (ms). */
  betSpeedTier2PostWinPadMs: 500,
  /** Min time before snap for tier-3 and hold-turbo (ms). */
  turboSnapMinSpinMs: 600,
  /** After snap, pause before expanding-wild (ms). */
  turboSnapResultHoldMs: 600,
  /** Minimum time reels stay in motion after a result arrives (ms). Stops “instant grid” when the player hit stop during the round-trip. */
  minReelSpinAnimationMs: 500,
  /**
   * Win celebration Spine: when true, tier gates are **1× / 2× / 5× / 10×** (big→huge→mega→epic) and the overlay can show from **1×** (QA only).
   * When false, production gates **5× / 10× / 20×** (big / mega / epic; huge is a build-up clip), overlay from **5×**.
   */
  winCelebrationTestTiers: false,
  /**
   * Last-win count-up duration (wins below 5× celebration threshold): fixed seconds for every win.
   */
  lastWinRollupDurationSec: 1,
  cruiseSpeedFactor: 22,

  startingBalance: 10_000,
  defaultBet: 10,
  minBet: 1,
  maxBet: 500,
  /** Max win multiplier from InitialState (maxOdd); max payout = maxBet × maxOdd. */
  maxOdd: null,

  /**
   * Symbol definitions with Spine animation mappings.
   * 
   * Each symbol must have a 'spine' property that matches a .json file in /public/assets/spines/
   * Available spine files: 7, bell, cherry, coin, grape, Lemon, orange, Plum, watermellon, wild, scatter
   * 
   * TO CHANGE WHICH SPINE ANIMATION IS USED FOR A SYMBOL:
   * Simply change the 'spine' property value to match the desired spine file name.
   * Example: To use the '7' animation for symbol 0, change spine: 'coin' to spine: '7'
   * 
   * Note: The seven ('7') animation is currently mapped to symbol id 1
   */
  symbols: [
    { id: 1, label: '1', color: 0x3b6fd9, spine: '7' },  // Seven animation
    { id: 2, label: '2', color: 0x2eb87a, spine: 'grape' },
    { id: 3, label: '3', color: 0xe8a317, spine: 'watermellon' },
    { id: 4, label: '4', color: 0xff6b9d, spine: 'bell' },
    { id: 5, label: '5', color: 0xffd447, spine: 'cherry' },
    { id: 6, label: '6', color: 0x22d3ee, spine: 'Plum' },
    { id: 7, label: '7', color: 0x38bdf8, spine: 'orange' },
    { id: 8, label: '8', color: 0x818cf8, spine: 'Lemon' },
    { id: 9, label: '9', color: 0x34d399, spine: 'wild' },
    { id: 10, label: '10', color: 0xf59e0b, spine: 'coin' },
    { id: 11, label: '11', color: 0xf472b6, spine: 'scatter' },
  ],

  /**
   * Ten standard 5×3 paylines (row index 0 = top, 2 = bottom per reel).
   */
  paylines: [
    [1, 1, 1, 1, 1], // 1
    [0, 0, 0, 0, 0], // 2
    [2, 2, 2, 2, 2], // 3
    [0, 1, 2, 1, 0], // 4
    [2, 1, 0, 1, 2], // 5
    [0, 0, 1, 2, 2], // 6
    [2, 2, 1, 0, 0], // 7
    [1, 2, 2, 2, 1], // 8
    [1, 0, 0, 0, 1], // 9
    [0, 1, 1, 1, 0], // 10
  ],

  /** Stage clear color (fallback) */
  backgroundColor: 0x090b10,
  machineFrameColor: 0x121826,
  reelMaskAlpha: 0.08,

  /**
   * Modern slate / cyan accent — shared tokens for canvas + HTML chrome.
   */
  theme: {
    fontDisplay: '"Syne", system-ui, sans-serif',
    fontUi: '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif',
    /** Symbol tile inset (matches textureFactory pad) */
    symbolTexturePad: 0,
    /** Primary accent (borders, chips, highlights) */
    gold: 0x38bdf8,
    goldBright: 0xf8fafc,
    goldDim: 0x0e7490,
    lightning: 0xe0f2fe,
    chrome: 0x94a3b8,
    marble: 0xe2e8f0,
    marbleShadow: 0x475569,
    /** Control bar — cool glass stack */
    panelRimHi: 0x1e293b,
    panelDeep: 0x0c1018,
    panelMid: 0x151c28,
    panelBorder: 0x334155,
    panelGlass: 0x243044,
    panelShadow: 0x020617,
    /** Stat card surfaces (unused when cards hidden; kept for tools / future) */
    cardSurfaceHi: 0x1e293b,
    cardSurfaceMid: 0x0f172a,
    cardSurfaceLo: 0x020617,
    cardHeroHi: 0x1e3a4f,
    cardHeroMid: 0x0f172a,
    cardHeroLo: 0x020617,
    /** ± chip button */
    chipFaceHi: 0x334155,
    chipFaceMid: 0x1e293b,
    chipFaceLo: 0x0f1419,
    reelWell: 0x06080d,
    /** Multiply tint on symbols not part of the current win line */
    reelSymbolDimTint: 0x5c6b7c,
    reelInnerLine: 0x475569,
    headerShine: 0x334155,
    skyGlow: 0x22d3ee,
    /** Radial backdrop (GameApp stage gradient) */
    ambientGlow: 0x38bdf8,
    cloudTint: 0x64748b,
    spinCore: 0x0891b2,
    spinCoreHi: 0x22d3ee,
    spinCoreLo: 0x0c4a6e,
    spinRing: 0x67e8f9,
    /** Typography on chrome */
    textLabel: 0xcbd5e1,
    textMuted: 0x94a3b8,
    textBright: 0xf8fafc,
    balanceGlow: 0x22d3ee,
    balanceFigure: 0xf8fafc,
    winGlow: 0xfbbf24,
    danger: 0xf87171,
    /** Payline strokes */
    paylineColors: [
      0xff1744, // red
      0x00e676, // green
      0x2979ff, // blue
      0xffea00, // yellow
      0xd500f9, // purple
      0xff9100, // orange
      0x00e5ff, // cyan
      0x76ff03, // lime
      0xff4081, // pink
      0x651fff, // deep violet
    ],
  },

  /**
   * Viewport tuning — applied in GameApp `layout()` (uniform Pixi `world` scale).
   */
  viewport: {
    /**
     * Multiplies world scale on **portrait** tablets only. `1` disables.
     * Portrait tablet = wider than narrow-phone breakpoint (760px) and taller than wide.
     * Landscape tablets are skipped — the 1922×1080 design already fits a landscape
     * tablet without enlargement, so a multiplier here would just zoom and crop content.
     */
    tabletWorldScaleMul: 1.50,
    /**
     * Multiplies world scale on **landscape** tablets only. `1` disables.
     * Slightly below 1 shrinks the whole game uniformly so the corner chrome icons
     * (home / sound / menu / fullscreen) gain a margin from the screen edges and
     * don't sit flush against them. Lower = more margin.
     */
    tabletLandscapeWorldScaleMul: 0.92,
    /**
     * Extra Y on the Pixi `world` position for **portrait** tablets only (logical 1922×1080 space).
     * Negative = shift the whole game upward on screen. Skipped in tablet landscape
     * to keep the game vertically centered when no world up-scale is applied.
     */
    tabletWorldOffsetY: -150,
    /**
     * Canvas bottom strip only (balance / quick bets / icons / portrait stacked HUD): extra Y for tablets.
     * Positive = move that strip lower on screen. Does not affect reel cabinet or landscape spin beside reels.
     */
    tabletBottomMenuOffsetY: 80,

    /** Portrait stacked HUD (tablet only): extra px between merged stats row and quick-bet strip. */
    tabletPortraitGapStatsToQbExtra: 18,
    /** Portrait stacked HUD (tablet only): extra px between quick-bet strip and SPIN / bet row. */
    tabletPortraitGapQbToSpinExtra: 28,
    /** Portrait stacked HUD (tablet only): extra px above bottom corner icons (home / sound / menu). */
    tabletPortraitBottomIconsGapExtra: 36,

    /** Landscape tablet: push corner icons slightly lower for air under the QB strip row. */
    tabletLandscapeBottomIconsGapExtra: 12,
    /** Landscape tablet: pull corner icons inward toward the balance / LAST WIN panels (logical px). */
    tabletLandscapeBottomIconsTowardCenterPx: 28,

    /**
     * Desktop landscape only (not tablet / phone): pull the four corner icons
     * (home / fullscreen on the left, sound / menu on the right) inward toward
     * the balance / LAST WIN panels so they keep a gap from the screen corners.
     * Positive = closer to the horizontal center.
     */
    desktopBottomIconsTowardCenterPx: 56,

    /**
     * Portrait tablet (desktop-style balance / LAST WIN cards): nudge both panels toward screen center (logical px).
     * Balance shifts right, LAST WIN shifts left by this amount.
     */
    tabletPortraitStatPanelsTowardCenterPx: 22,

    /** Portrait stacked HUD (tablet only): shift quick-bet strip left (logical px). */
    tabletPortraitQuickBetShiftLeftPx: 120,

    /** Portrait tablet: multiply corner icon size (home / sound / menu); below 1 shrinks. */
    tabletPortraitCornerIconScaleMul: 0.82,
    /** Portrait tablet: push corner icon pairs toward the left/right edges (logical px). */
    tabletPortraitCornerIconsAwayFromCenterPx: 95,

    /**
     * Tablet (`_isTabletViewport`): multiplier on win celebration (stacked with portrait branch if any).
     * Below 1 = smaller on tablets.
     */
    tabletWinCelebrationScaleMul: 0.85,
    /** Narrow portrait phone (width ≤760): multiplier on the lerped celebration scale. */
    narrowPortraitWinCelebrationMul: 0.88,
    /** Portrait width 760–1024 (e.g. tablet portrait): scales the `0.76` baseline. */
    portraitTabletWinCelebrationMul: 0.9,
    /**
     * Desktop / large landscape (not tablet, not portrait with width ≤1024): extra celebration size. `1` = default.
     */
    desktopWinCelebrationScaleMul: 1.14,
    /** Tablet: move win celebration root down in logical 1922×1080 space (positive = lower). */
    tabletWinCelebrationOffsetYPx: 60,
  },

  animations: {
    idle: 'idle',
    win: 'win',
    noWin: 'noWin',
    /** Reel spin; falls back to {@link gameConfig.animations.spinningLegacy} if missing in skeleton */
    spinning: 'spinning',
    /** Common export name when the clip is not named `spinning` */
    spinningLegacy: 'blur',
    press: 'press',
    release: 'release',
  },

  adapter: {
    /** 'pulse' = very subtle idle breathing on symbols */
    symbolIdleMode: 'pulse',
    idlePulseScale: 1.007,
    idlePulseDuration: 2.75,
    /** Win loop: pop → wiggle → settle → light shimmer */
    winScalePeak: 1.13,
    winPopDuration: 0.27,
    winSettleDuration: 0.38,
    winRotationWiggleRad: 0.062,
    winShimmerAlphaMin: 0.9,
    /** Delay between symbols when a line wins (seconds, left-to-right along cells) */
    winStaggerSec: 0.045,
    /** Minimum seconds each payline stays on screen while cycling (actual hold ≥ Spine `win` clip length). */
    winLinePhaseHoldSec: 0.92,
    buttonPressScale: 1,
    buttonTweenDuration: 0.12,
  },

  /** Fraction of cell height — roll past the lock line before easing back (larger = longer arc). */
  reelLandBounceRatio: 0.30,
  /** Min seconds for that overshoot deceleration segment (tier speed scales it). */
  reelLandBounceIn: 0.05,
  /** Seconds for the snap-back to aligned stop; capped vs spinPhaseDecelerate budget. */
  reelLandBounceOut: 0.10,

  spinDistanceRatios: {
    accelerate: 0,
    cruise: 0.7,
    decelerate: 0.3,
  },

  debug: {
    defaultEnabled: false,
    showCellIndices: true,
  },
};
